import type { ComponentType } from 'react';
import { getBlogSlugs, slugFromPath } from '../../utils/blog-slugs';

export interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  dateModified?: string;
  description: string;
  keywords?: string[];
  metaDescription?: string;
  image?: string;
}

type BlogPostMetaWithoutSlug = Omit<BlogPostMeta, 'slug'>;

// Load all MDX modules eagerly so we only import each file once.
//
// IMPORTANT:
// - In Vite dev, `import.meta.glob` is a function.
// - In Vite build, the `import.meta.glob()` call is replaced at build time with
//   a plain object mapping file paths -> modules, so we must not guard this
//   behind a runtime `typeof import.meta.glob === 'function'` check.
//
// This module is intended for the app runtime (Vite/Vite build output). Our
// Node-only scripts (sitemap/prerender) discover slugs via the filesystem.
const modules = import.meta.glob('./*.mdx', {
  eager: true,
}) as Record<
  string,
  {
    default: ComponentType<Record<string, unknown>>;
    meta?: BlogPostMetaWithoutSlug;
  }
>;

const modulesBySlug = new Map(
  Object.entries(modules).map(([p, mod]) => [slugFromPath(p), mod]),
);

export const posts: BlogPostMeta[] = getBlogSlugs(modules)
  .map((slug) => {
    const mod = modulesBySlug.get(slug);
    if (!mod?.meta) return undefined;
    return {
      slug,
      ...mod.meta,
    };
  })
  .filter((p): p is BlogPostMeta => Boolean(p))
  // Newest first
  .sort((a, b) => b.date.localeCompare(a.date) || b.slug.localeCompare(a.slug));

export function getPostBySlug(slug: string): BlogPostMeta | undefined {
  return posts.find((p) => p.slug === slug);
}

export function getPostComponent(
  slug: string,
): ComponentType<Record<string, unknown>> | undefined {
  const path = `./${slug}.mdx` as const;
  const mod = modules[path];
  return mod?.default;
}
